import { EON_VIDEO_ID, GameLaunchStatus } from "@onmo/eon-streaming";
import { LoadingStatuses } from "../constants/gamePlay";
import { TOAST_MESSAGE_VIDEO_IDS } from "../constants/videos";
import { getGuideVideoUrl } from "./guideVideos";
import { StreamErrorType } from "../types/gamePlay";
import {
  NetworkConditionStatus,
  NetworkQuality,
  StreamQualityStatus,
} from "../types/network";
import { isTV } from "../utils/platform";
import BadNetwork from "../assets/icons/bad-network.svg";
import MediumNetwork from "../assets/icons/medium-network.svg";
import GoodNetwork from "../assets/icons/good-network.svg";

export const getLoadingGameMessage = (
  loadingStatus?: LoadingStatuses,
  showLaunching?: boolean,
) => {
  if (showLaunching && loadingStatus === "Launching") {
    return {
      type: "snackbar",
      titleKey: "GamePlay_Your Game Is Installed",
      icon: "icon-bold-tick-circle",
      status: GameLaunchStatus.NONE,
    };
  } else if (loadingStatus === "Done") {
    return {
      type: "snackbar",
      titleKey: "GamePlay_Your Console is Ready!",
      icon: "icon-bold-tick-circle",
      status: GameLaunchStatus.NONE,
    };
  }
};

const getBaseMessage = (status: GameLaunchStatus) => {
  switch (status) {
    case GameLaunchStatus.STATUS_INSTALLING:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Console Ready",
        icon: "icon-bold-tick-circle",
        status,
      };

    case GameLaunchStatus.STATUS_STEAMLOGIN:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Steam Login",
        descriptionKey: "GamePlay_Select profile or login to Steam",
        status,
      };

    case GameLaunchStatus.STATUS_SETLAUNCHEROPTIONS:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Optimizing Console...",
        icon: "icon-loading_circle",
        status,
      };

    case GameLaunchStatus.STATUS_LAUNCHER_UP:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Game Launcher Active",
        descriptionKey: "GamePlay_Check and interact if needed to continue",
        status,
      };

    case GameLaunchStatus.STATUS_EULA:
      return {
        type: "snackbar",
        titleKey: "GamePlay_EULA",
        descriptionKey: "GamePlay_Accept End User License Agreement",
        status,
      };

    case GameLaunchStatus.STATUS_NOGAMELICENSE:
      return {
        type: "popup",
        titleKey: "GamePlay_License Required",
        descriptionKey: isTV
          ? "GamePlay_You don’t own this game. Please scan the QR code below to purchase it to start playing."
          : "GamePlay_You don’t own this game. Purchase it to start playing.",
        status,
      };

    case GameLaunchStatus.STATUS_CONSENTNOCLOUDSYNC:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Cloud Sync Issue",
        descriptionKey: "GamePlay_User Input Needed",
        status,
      };

    case GameLaunchStatus.STATUS_GAMELAUNCH_TIMEOUT:
      return {
        type: "popup",
        titleKey: "GamePlay_Session Timed Out",
        descriptionKey:
          "GamePlay_The game didn’t start within the expected time and has been closed. Please try again.",
        status,
      };

    case GameLaunchStatus.STATUS_GAMELAUNCHING:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Launching Game...",
        icon: "icon-loading_circle",
        descriptionKey:
          "GamePlay_Configuring your Smart Console and loading game assets",
        status,
      };

    case GameLaunchStatus.STATUS_GAMEEXITED:
      return {
        type: "popup",
        titleKey: "GamePlay_Game Closed",
        descriptionKey:
          "GamePlay_Thanks for playing! Your game session has ended.",
        status,
      };

    case GameLaunchStatus.ERR_NOTACTIVE:
      return {
        type: "popup",
        titleKey: "GamePlay_Game Closed",
        descriptionKey:
          "GamePlay_The game was closed due to inactivity. Ready to play? Jump back in or try another title.",
        status,
      };

    case GameLaunchStatus.STATUS_SYNCONEXIT:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Shutting Down Console...",
        icon: "icon-loading_circle",
        status,
      };

    case GameLaunchStatus.ERROR:
      return {
        type: "popup",
        titleKey: "GamePlay_Session Inactive",
        descriptionKey:
          "GamePlay_Your session has ended. Please restart the game!",
        status,
      };

    case GameLaunchStatus.NETWORK_ERROR:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Network Unstable",
        descriptionKey:
          "GamePlay_Check your network connection or try to restart the game session",
        icon: "icon-network-alert",
        status,
      };

    case GameLaunchStatus.NETWORK_DISCONNECTED:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Network Disconnected",
        descriptionKey: "GamePlay_You're offline. Check your Wi-Fi or cable.",
        icon: "icon-network-alert",
        status,
      };

    case GameLaunchStatus.STATUS_STEAMLOGIN_SUCCESS:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Steam Login Success!",
        icon: "icon-bold-tick-circle",
        status,
      };

    case GameLaunchStatus.STATUS_GAMERUNNING_UPDATEPENDING:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Maintenance Alert",
        descriptionKey:
          "GamePlay_Shutting down in 5 minutes, please save and exit now",
        status,
      };

    case GameLaunchStatus.STATUS_UPDATEDETECTED:
      return {
        type: "popup",
        titleKey: "GamePlay_Update Required",
        descriptionKey:
          "GamePlay_Steam started an update — your session was closed. Please relaunch the game after the update completes.",
        status,
      };

    case GameLaunchStatus.STATUS_GAMERUNNING:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Game is Ready",
        icon: "icon-bold-tick-circle",
        descriptionKey: "GamePlay_Access Console Options anytime — learn how.",
        status,
      };

    case GameLaunchStatus.REMOTE_INPUT_REQUIRED:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Put Aside Your Controller",
        descriptionKey: "GamePlay_Use TV remote control to use the keyboard",
        status,
      };

    case GameLaunchStatus.STATUS_INACTIVITY_TIMEOUT:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Inactivity Warning!",
        descriptionKey:
          "GamePlay_Your session is about to be terminated. Press anything to continue playing",
        icon: "icon-bold-danger",
        status,
      };
  }
};

export const getNetworkConditionMessage = (status: NetworkConditionStatus) => {
  switch (status) {
    case NetworkConditionStatus.PACKET_LOSS:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Packet Loss",
        descriptionKey:
          "GamePlay_Connection is dropping. You may see stutters or glitches.",
        icon: "icon-packet-lost",
        status,
      };

    case NetworkConditionStatus.LATENCY_VARIATION:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Latency Variation",
        descriptionKey:
          "GamePlay_Unstable connection. Try a wired connection or move closer to your router.",
        icon: "icon-latency-variation",
        status,
      };

    case NetworkConditionStatus.HIGH_LATENCY:
      return {
        type: "snackbar",
        titleKey: "GamePlay_High Latency",
        descriptionKey:
          "GamePlay_Slow connection. Try a wired connection or move closer to your router.",
        icon: "icon-high-latency",
        status,
      };
  }
};

export const getStreamQualityMessage = (status: StreamQualityStatus) => {
  switch (status) {
    case StreamQualityStatus.STREAM_QUALITY_OPTIMIZED:
      return {
        type: "snackbar",
        titleKey: "GamePlay_Stream Quality Optimised",
        descriptionKey:
          "GamePlay_We've optimised the stream quality to match your device's performance.",
        icon: "icon-stream-optimized",
        status,
      };
  }
};

export const getPcGameMessage = (status: GameLaunchStatus) => {
  const baseMessage = getBaseMessage(status);

  if (!baseMessage) return;

  const videoURL = getGuideVideoUrl(TOAST_MESSAGE_VIDEO_IDS[status]);
  return videoURL ? { ...baseMessage, videoURL } : baseMessage;
};

export const getStreamErrorMessage = (streamError?: StreamErrorType) => {
  switch (streamError) {
    case "playing":
      return {
        titleKey: "GamePlay_Another Game is Running",
        descriptionKey:
          "GamePlay_You can only play one game at a time. Starting this game will automatically close your previous session.",
      };
    case "exit":
      return {
        titleKey: "GamePlay_Game Closed",
        descriptionKey:
          "GamePlay_Thanks for playing! Your game session has ended.",
      };
    case "err_updatinggame":
      return {
        titleKey: "GamePlay_Maintenance",
        descriptionKey:
          "GamePlay_This game is currently under maintenance. Please try again later.",
      };
    case "server_error":
      return {
        titleKey: "GamePlay_Servers Packed",
        descriptionKey:
          "GamePlay_All our servers are full right now—hang tight and try again soon!",
      };
    case "sync_in_progress":
      return {
        titleKey: "GamePlay_Saving Your Progress",
        descriptionKey:
          "GamePlay_Your latest save is being synced. Please wait for a few minutes before launching this game again or try a different game.",
      };
    case "payment_failed":
    case "error":
    default:
      return {
        titleKey: "GamePlay_Unable to Load Game",
        descriptionKey:
          "GamePlay_We encountered an issue loading your game. Please restart and try again. If the problem persists, check your connection and try later.",
      };
  }
};

export const requestPointerLockForPCGame = () => {
  if (!document.pointerLockElement) {
    const pcStreamVideo = document.getElementById(EON_VIDEO_ID);
    if (pcStreamVideo) {
      try {
        pcStreamVideo.requestPointerLock();
        console.log("Pointer lock activated");
      } catch (error) {
        console.error("Error request pointer lock:", error);
      }
    }
  }
};

export const exitPointerLock = () => {
  try {
    if (document.pointerLockElement) {
      document.exitPointerLock();
      console.log("Pointer lock deactivated");
    }
  } catch (error) {
    console.error("Error exiting pointer lock:", error);
  }
};

export const getNetworkIcon = (
  networkQuality: NetworkQuality,
  isNetworkDisconnected?: boolean,
) => {
  if (isNetworkDisconnected) {
    return BadNetwork;
  }

  switch (networkQuality) {
    case NetworkQuality.Good:
      return GoodNetwork;
    case NetworkQuality.Medium:
      return MediumNetwork;
    case NetworkQuality.Bad:
      return BadNetwork;
    default:
      return GoodNetwork;
  }
};
